import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import * as extract from 'extractFromState';
import * as comActions from 'actions/CommerceWizardActions';
import * as actions from 'actions/index';
import * as triggers from 'constants/commerceTriggers';
import * as S from '../CommerceWizard.styles';
import { commerceRecoViewed, commerceRecoCopied } from '../../../../analytics/mlEventAnalytics';
import { updatePreviouslyRecommendedProducts } from 'api/productRecommendations';
import { DraftHelpers } from 'util/Draft';
import { ToolTipFixedWidth } from 'components/common/tooltips';
import CWProduct from '../CWProduct/CWProduct';
import { handleError } from 'services/ErrorService/ErrorService';
import LoadingComponent, { LoadingWrapper } from 'components/LoadingComponent/LoadingComponent';
import { showExpressError } from 'util/errorPopupUtil';
import get from 'lodash/get';
import * as detectionTypes from 'constants/commerceDetectionTypes';
import { ConfirmationButton } from './CWSelectedRecommendation.styles';
import { findMacroSnippetByProductId } from 'api/macroSnippets';

export const CWSelectedRecommendation = (props) => {
  const [loading, setLoading] = useState(false);
  const [customProductSnippet, setCustomProductSnippet] = useState(null);

  // TODO: Fix dependency array
  useEffect(() => {
    const getDetectedProductOverrideSnippet = (macroSnippet) => {
      switch (props.detectionSource) {
        case detectionTypes.PRODUCT_MENTIONED:
          return macroSnippet.mentionedProductSnippet;
        case detectionTypes.RECENT_ORDER:
          return macroSnippet.recentOrderSnippet;
        default:
          return null; // Global fallback does not apply here
      }
    };

    const handleGetMacroSnippets = async () => {
      try {
        setLoading(true);
        const params = {
          simplrAccountId: props.simplrAccountId,
          productId: props.selectedRecommendation && props.selectedRecommendation.id,
          type: 'COMMERCE',
        };

        const macroSnippet = await findMacroSnippetByProductId(params);
        if (macroSnippet) {
          const snippet = getDetectedProductOverrideSnippet(macroSnippet);
          if (snippet) {
            setCustomProductSnippet(snippet);
          }
        }
      } catch (error) {
        showExpressError(error);
      } finally {
        setLoading(false);
      }
    };

    handleGetMacroSnippets();
    commerceRecoViewed();
  }, []); // eslint-disable-line

  const getProductPrice = () => {
    return props.selectedRecommendation.price && Math.floor(props.selectedRecommendation.price).toFixed(0);
  };

  const getPartnerMacroSnippet = () => {
    const section = props.brand ? props.brand : 'common';
    const mentionedProductSnippet = get(props, `conversationalCommerce.${section}.mentionedProductSnippet]`);
    const recentOrderSnippet = get(props, `conversationalCommerce.${section}.recentOrderSnippet`);
    const globalFallbackSnippet = get(props, `conversationalCommerce.${section}.globalFallbackSnippet`);

    switch (props.detectionSource) {
      case detectionTypes.PRODUCT_MENTIONED:
        return mentionedProductSnippet;
      case detectionTypes.RECENT_ORDER:
        return recentOrderSnippet;
      case detectionTypes.GLOBAL_FALLBACK:
        return globalFallbackSnippet;
      default:
        return null;
    }
  };

  const getMacroText = () => {
    const partnerMacroSnippet = getPartnerMacroSnippet(); // cross-partner custom snippet
    const customizedSnippet = customProductSnippet || partnerMacroSnippet; // product-specific vs partner-wide

    const currentProductTitle = get(props, 'customerProduct.title');
    const recommendationTitle = get(props, 'selectedRecommendation.title');
    const recommendationLink = get(props, 'selectedRecommendation.productLink');

    if (customizedSnippet) {
      return customizedSnippet
        .replace('CURRENT_PRODUCT', currentProductTitle)
        .replace('RECOMMENDATION', recommendationTitle)
        .replace('LINK', recommendationLink);
    }

    switch (props.detectionSource) {
      case detectionTypes.PRODUCT_MENTIONED:
      case detectionTypes.RECENT_ORDER:
        return `If you're interested in {${currentProductTitle}}, I think you may also want to have a look at {${recommendationTitle}} before you check out! ${recommendationLink}\n\n`;
      case detectionTypes.GLOBAL_FALLBACK:
        return `Have you already checked out our {${recommendationTitle}}? We've gotten a lot of great feedback from happy customers about {it/them}! ${recommendationLink}\n\n`;
      default:
        return null;
    }
  };

  const handleCopyMacro = async () => {
    try {
      setLoading(true);
      const macro = getMacroText();
      let newEditorState;
      const newText = props.editorState.getCurrentContent().getPlainText();
      const newTextIncludesProductTrigger = newText.includes(triggers.RECOMMEND_A_PRODUCT_TRIGGER);
      if (newTextIncludesProductTrigger) {
        newEditorState = DraftHelpers.replaceRegularExpression(
          macro,
          props.editorState,
          triggers.RECOMMEND_A_PRODUCT_TRIGGER,
          false
        );
      } else {
        // for chat tickets simply append macro to contents in case they already have text there
        newEditorState = DraftHelpers.appendHtmlToContents(macro, props.editorState);
      }

      // save product on conversation for future reference
      await updatePreviouslyRecommendedProducts(props.selectedRecommendation, props.selectedConversationId);
      props.saveNewMessageEditorState(props.selectedConversationId, newEditorState);
      props.setCenterPaneExtraInfoPanelActive(false);
      commerceRecoCopied(macro);
    } catch (error) {
      handleError({ error, customMessage: 'There was a problem copying this snippet', showPopup: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {props.selectedRecommendation && !loading ? (
        <>
          <CWProduct
            headerText={'This is a product you can recommend'}
            image={props.selectedRecommendation.image}
            productLink={props.selectedRecommendation.productLink}
            description={props.selectedRecommendation.description}
            price={getProductPrice()}
            title={props.selectedRecommendation.title}
          />
          <S.MacroContainer>
            <h4>
              <img src={'/images/commerce/macro-suggestion-icon.svg'} alt="icon" />
              Macro Suggestion
              <ToolTipFixedWidth
                arrow
                placement="bottom"
                content={`When you press copy, this snippet will be automatically
                inserted into the macro you previously selected.`}
              >
                <img src={'/images/tooltip-icon.svg'} alt="icon" />
              </ToolTipFixedWidth>
            </h4>
            <p>{getMacroText()}</p>
            <ConfirmationButton onClick={() => handleCopyMacro()} fullColored={true}>
              Insert
            </ConfirmationButton>
          </S.MacroContainer>
        </>
      ) : (
        <LoadingWrapper>
          <LoadingComponent />
        </LoadingWrapper>
      )}
    </>
  );
};

CWSelectedRecommendation.propTypes = {
  customerProduct: PropTypes.object,
  selectedRecommendation: PropTypes.shape({
    productLink: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
  }).isRequired,
  conversationalCommerce: PropTypes.object,
  setCommerceWizardPath: PropTypes.func.isRequired,
  saveNewMessageEditorState: PropTypes.func.isRequired,
  selectedConversationId: PropTypes.string.isRequired,
  editorState: PropTypes.object.isRequired,
  setCenterPaneExtraInfoPanelActive: PropTypes.func.isRequired,
  simplrAccountId: PropTypes.string.isRequired,
  detectionSource: PropTypes.string.isRequired,
  brand: PropTypes.string,
};

const mapStateToProps = (state) => ({
  customerProduct: extract.extractCommerceWizardProductDetected(state),
  selectedRecommendation: extract.extractCommerceWizardSelectedRecommendation(state),
  conversationalCommerce: extract.extractConversationCommerceDataOfSelectedTicket(state),
  selectedConversationId: extract.extractNonHistoricalTicketIdFromState(state),
  editorState: extract.getNewMessagesEditor(state),
  simplrAccountId: extract.getSimplrAccountIdOfSelectedTicket(state),
  detectionSource: extract.extractCommerceWizardDetectionSource(state),
  brand: extract.getBrandOfSelectedTicket(state),
});

const mapDispatchToProps = {
  setCommerceWizardPath: comActions.setCommerceWizardPath,
  saveNewMessageEditorState: actions.saveNewMessageEditorState,
  setCenterPaneExtraInfoPanelActive: actions.setCenterPaneExtraInfoPanelActive,
};

export default connect(mapStateToProps, mapDispatchToProps)(CWSelectedRecommendation);
